/**
 * Tamagui configuration for AS Life.
 *
 * Built on the official `@tamagui/config/v4` preset (React 18 compatible)
 * and overlaid with the AS Life brand from `design_handoff_as_life_portal`:
 * navy primary + bronze accent + semantic status tones.
 *
 * Brand-named keys are added to the base `light` / `dark` themes so they are
 * typed and resolvable in components (`$primary`, `$tone_green`, …),
 * and the base surfaces are overridden to the handoff values. Object spreads
 * (not a loop) are used so Tamagui keeps full type inference for style props.
 *
 * Fonts and the radius/space/size token scales are left as the preset
 * defaults for now — refine these while building the real screens.
 */
import { defaultConfig } from '@tamagui/config/v4'
import { createTamagui } from 'tamagui'

import { navy, tones, surfacesLight, surfacesDark, toneTextLight, toneTextDark } from './src/theme/brand'
import { accentThemes } from './src/theme/accents'

// Brand keys shared by both base themes. Dark differs only where a value
// depends on the surface (e.g. primary reads brighter on dark chrome).
const brandLight = {
  primary: navy.navy9,
  primaryActive: navy.navyActive,
  primaryBg: navy.navyBgLight,
  // Text/icon colour for content on top of $primary (buttons, badges, chips).
  // Light: every accent is a dark hex → white. Dark: primary is the accent's
  // `bright` (50% white) → dark ink (see brandDark). Accent sub-themes inherit
  // it unchanged — see src/theme/accents.ts.
  onPrimary: '#fff',

  tone_green: tones.green,
  tone_red: tones.red,
  // readable tone text per theme — what `tone(k).text` resolves to (src/theme/tones.ts)
  tone_blue_text: toneTextLight.blue,
  tone_green_text: toneTextLight.green,
  tone_gold_text: toneTextLight.gold,
  tone_red_text: toneTextLight.red,
  tone_purple_text: toneTextLight.purple,
  tone_slate_text: toneTextLight.slate,

  bgLayout: surfacesLight.bgLayout,
  bgElevated: surfacesLight.bgElevated,
  borderSecondary: surfacesLight.borderSecondary,
  muted: surfacesLight.muted, // secondary text/icons (#64748B)
  tertiary: '#94A3B8', // tertiary text/icons (one notch lighter than muted)
} as const

const brandDark = {
  ...brandLight,
  primary: navy.navyBright,
  onPrimary: surfacesDark.bgLayout, // white on #8EA1C4 is 2.6:1; dark ink ≈6.5:1
  primaryActive: accentThemes.dark_navy.primaryActive,
  primaryBg: navy.navyBgDark,

  bgLayout: surfacesDark.bgLayout,
  bgElevated: surfacesDark.bgElevated,
  borderSecondary: surfacesDark.borderSecondary,
  muted: surfacesDark.muted,
  tertiary: surfacesDark.tertiary, // dimmer than muted on dark surfaces

  tone_blue_text: toneTextDark.blue,
  tone_green_text: toneTextDark.green,
  tone_gold_text: toneTextDark.gold,
  tone_red_text: toneTextDark.red,
  tone_purple_text: toneTextDark.purple,
  tone_slate_text: toneTextDark.slate,
} as const

const themes = {
  ...defaultConfig.themes,
  light: {
    ...defaultConfig.themes.light,
    ...brandLight,
    background: surfacesLight.bgBase,
    backgroundHover: '#F8FAFC',
    backgroundPress: '#F1F5F9',
    backgroundFocus: '#F1F5F9',
    color: surfacesLight.text,
    borderColor: surfacesLight.border,
    placeholderColor: surfacesLight.muted,
  },
  dark: {
    ...defaultConfig.themes.dark,
    ...brandDark,
    background: surfacesDark.bgBase,
    backgroundHover: surfacesDark.bgElevated,
    backgroundPress: surfacesDark.bgPress,
    backgroundFocus: surfacesDark.bgPress,
    color: surfacesDark.text,
    borderColor: surfacesDark.border,
    placeholderColor: surfacesDark.muted,
  },
  // Accent sub-themes: `light_<key>` / `dark_<key>` override only the primary
  // family, so a nested <Theme name={accent}> re-skins $primary app-wide while
  // every other token inherits from light/dark. See src/theme/accents.ts.
  ...accentThemes,
}

export const config = createTamagui({
  ...defaultConfig,
  themes,
  settings: {
    ...defaultConfig.settings,
    // The v4 preset enforces shorthand-only props (bg/items/rounded/…). Allow
    // standard longhands too (backgroundColor/alignItems/borderRadius/…) so
    // screen code reads like normal React Native / Tamagui.
    onlyAllowShorthands: false,
    // v4 defaults to "somewhat-strict-web", which rejects raw hex/rgba colour
    // strings. Semantic tones (see src/theme/tones.ts) are computed colours, so
    // loosen value typing — theme tokens ($primary, …) still resolve as before.
    allowedStyleValues: false,
  },
})

export default config

export type AppConfig = typeof config

declare module 'tamagui' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface TamaguiCustomConfig extends AppConfig {}
}
